/**
 * Account management API client.
 * Calls sam-dbam /dbam/v1/organizations/* and /dbam/v1/system/* endpoints directly.
 * Follows the same pattern as consoleApi.js.
 */
import { getOrgContext } from './cloudApi';

function getBaseUrl() {
    const url = process.env.REACT_APP_SAM_DBAM_URL;
    if (!url) throw new Error('REACT_APP_SAM_DBAM_URL not set');
    return url.replace(/\/+$/, '') + '/dbam/v1';
}

async function request(path, { method = 'GET', headers = {}, body } = {}) {
    const url = `${getBaseUrl()}${path}`;
    // Inject org context headers so the backend can scope to the caller's org.
    const ctx = getOrgContext();
    const orgH = {};
    if (ctx.orgId) orgH['X-Org-ID'] = ctx.orgId;
    if (ctx.userId) orgH['X-User-ID'] = ctx.userId;
    const init = {
        method,
        headers: { Accept: 'application/json', ...orgH, ...headers },
    };
    if (body !== undefined) {
        init.headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(body);
    }

    const res = await fetch(url, init);
    if (res.status === 204) return null;

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const rawData = isJson ? await res.json() : await res.text();

    if (!res.ok) {
        let message;
        if (rawData && typeof rawData === 'object') {
            if (rawData.error && typeof rawData.error === 'object') {
                message = rawData.error.message || rawData.error.code;
            } else {
                message = rawData.message || rawData.error;
            }
        }
        if (!message) message = `Request failed with status ${res.status}`;
        const err = new Error(message);
        err.status = res.status;
        err.data = rawData;
        throw err;
    }
    return rawData;
}

// ─── Organization ──────────────────────────────────────────────────

export async function getOrganization(orgId) {
    const resp = await request(`/organizations/${encodeURIComponent(orgId)}`);
    return resp?.data || resp;
}

export async function updateOrganization(orgId, updates) {
    const resp = await request(`/organizations/${encodeURIComponent(orgId)}`, {
        method: 'PATCH',
        body: updates,
    });
    return resp?.data || resp;
}

// ─── Members ───────────────────────────────────────────────────────

export async function listMembers(orgId) {
    const resp = await request(`/organizations/${encodeURIComponent(orgId)}/members`);
    return resp?.data || resp || [];
}

/**
 * Update a user's preferences (JSONB) by user_id.
 * E.g. updateUserPreferences(userId, { passkey_required: true })
 */
export async function updateUserPreferences(userId, preferences) {
    const resp = await request(`/users/${encodeURIComponent(userId)}`, {
        method: 'PATCH',
        body: { preferences },
    });
    return resp?.data || resp;
}

/**
 * Update a user's profile fields by user_id. Accepts a partial object; only
 * provided keys change. Changing `email` also syncs the member's sign-in email
 * in the auth system server-side. Returns the updated user.
 * E.g. updateUser(userId, { name: 'Jane Doe', email: 'jane@acme.com' })
 */
export async function updateUser(userId, updates) {
    const resp = await request(`/users/${encodeURIComponent(userId)}`, {
        method: 'PATCH',
        body: updates,
    });
    return resp?.data || resp;
}

export async function addMember(orgId, userId, role = 'member') {
    return request(`/organizations/${encodeURIComponent(orgId)}/members`, {
        method: 'POST',
        body: { user_id: userId, role },
    });
}

export async function removeMember(orgId, userId) {
    return request(`/organizations/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}`, {
        method: 'DELETE',
    });
}

export async function updateMemberRole(orgId, userId, role) {
    return request(`/organizations/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}`, {
        method: 'PATCH',
        body: { role },
    });
}

/**
 * Create a user in sam-dbam (and Hanko, if configured).
 * Returns the created user object.
 */
export async function createUser(email, orgId, name) {
    const body = { email, org_id: orgId };
    if (name) body.name = name;
    const resp = await request('/users', {
        method: 'POST',
        body,
    });
    return resp?.data || resp;
}

/**
 * Invite a member: create user, then add to org with role.
 * If the user already exists (409), tries to add them as a member directly.
 */
export async function inviteMember(orgId, email, role = 'member', name) {
    let user;
    try {
        user = await createUser(email, orgId, name);
    } catch (err) {
        if (err.status === 409) {
            // User exists — look them up and add as member
            const resp = await request(`/users/by-email/${encodeURIComponent(email)}`);
            user = resp?.data || resp;
        } else {
            throw err;
        }
    }
    // Ensure org membership (createUser with org_id already does this, but handle 409 case)
    if (user?.user_id) {
        try {
            await addMember(orgId, user.user_id, role);
        } catch (memberErr) {
            // If already a member, that's fine
            if (memberErr.status !== 409) throw memberErr;
        }
    }
    return user;
}

// ─── Credentials ───────────────────────────────────────────────────

export async function listCredentials(orgId) {
    const resp = await request(`/organizations/${encodeURIComponent(orgId)}/credentials`);
    return resp?.data || resp || [];
}

export async function createCredential(orgId, payload) {
    const resp = await request(`/organizations/${encodeURIComponent(orgId)}/credentials`, {
        method: 'POST',
        body: payload,
    });
    return resp?.data || resp;
}

export async function deleteCredential(orgId, credentialId) {
    return request(`/organizations/${encodeURIComponent(orgId)}/credentials/${encodeURIComponent(credentialId)}`, {
        method: 'DELETE',
    });
}

// ─── AI Engine Config (WP-13) ──────────────────────────────────────

/**
 * Fetch the org's AI engine config. Always returns 200 (auto-creates a tier
 * default). The response is UI-complete: tier, current model/provider,
 * allowed_engines, full catalog, byom_supported, byom_configured (a custom model
 * is saved) and byom_working (it can actually serve traffic).
 */
export async function getAIConfig(orgId) {
    const resp = await request(`/organizations/${encodeURIComponent(orgId)}/ai-config`);
    return resp?.data || resp;
}

/**
 * Update the org's AI engine config. Body semantics:
 *   { model }                                   → select a catalog engine (tier-enforced)
 *   { model, custom_api_key, custom_endpoint_url? } → set BYOM (model name required)
 *   { custom_api_key: "" }                      → clear BYOM (off-ramp)
 * Errors surface as thrown Error with .status and .data.error.code.
 */
export async function patchAIConfig(orgId, body) {
    const resp = await request(`/organizations/${encodeURIComponent(orgId)}/ai-config`, {
        method: 'PATCH',
        body,
    });
    return resp?.data || resp;
}

/**
 * Test a custom model without saving it. Calls the endpoint and reports what
 * happened, so the customer finds out here instead of from a respondent.
 *
 * Any field left blank falls back to what is already stored, which lets someone
 * re-test a saved configuration without retyping their key.
 *
 * Returns a result object rather than throwing for an expected failure:
 *   { ok, reachable, chat_shape, contract, code, message, sample_text,
 *     extracted, http_status, latency_ms }
 */
export async function testBYOMConfig(orgId, { model, custom_endpoint_url, custom_api_key } = {}) {
    const resp = await request(`/organizations/${encodeURIComponent(orgId)}/ai-config/byom-test`, {
        method: 'POST',
        body: {
            model: model || '',
            custom_endpoint_url: custom_endpoint_url || '',
            custom_api_key: custom_api_key || '',
        },
    });
    return resp?.data || resp;
}

// ─── Service Configs ───────────────────────────────────────────────

export async function listServiceConfigs(orgId) {
    const resp = await request(`/organizations/${encodeURIComponent(orgId)}/services`);
    return resp?.data || resp || [];
}

export async function upsertServiceConfig(orgId, serviceType, payload) {
    const resp = await request(`/organizations/${encodeURIComponent(orgId)}/services/${encodeURIComponent(serviceType)}`, {
        method: 'PUT',
        body: payload,
    });
    return resp?.data || resp;
}

export async function deleteServiceConfig(orgId, serviceType) {
    return request(`/organizations/${encodeURIComponent(orgId)}/services/${encodeURIComponent(serviceType)}`, {
        method: 'DELETE',
    });
}

export async function getEffectiveConfig(orgId, serviceType) {
    const resp = await request(`/organizations/${encodeURIComponent(orgId)}/services/${encodeURIComponent(serviceType)}/effective`);
    return resp?.data || resp;
}

// ─── Logo Upload ───────────────────────────────────────────────────

export async function uploadLogo(orgId, file) {
    const base = getBaseUrl();
    const formData = new FormData();
    formData.append('logo', file);

    const ctx = getOrgContext();
    const logoHeaders = { Accept: 'application/json' };
    if (ctx.orgId) logoHeaders['X-Org-ID'] = ctx.orgId;
    if (ctx.userId) logoHeaders['X-User-ID'] = ctx.userId;
    const res = await fetch(`${base}/organizations/${encodeURIComponent(orgId)}/logo`, {
        method: 'POST',
        headers: logoHeaders,
        body: formData,
    });

    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error?.message || `Upload failed (${res.status})`);
    }
    const data = await res.json();
    return data?.data || data;
}

export async function deleteLogo(orgId) {
    return request(`/organizations/${encodeURIComponent(orgId)}/logo`, {
        method: 'DELETE',
    });
}

// ─── Billing ────────────────────────────────────────────────────────────────

/**
 * Fetch the minimal subscription/access state used by the Console paywall.
 * This endpoint intentionally returns no Stripe customer IDs, card details or
 * invoices, so every organization member can be gated without seeing billing
 * records reserved for the account owner.
 */
export async function getBillingAccess() {
    const res = await fetch('/api/console/billing/access', {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data?.message || data?.error || `Failed to verify account access (${res.status})`);
    }
    return data;
}

export async function startBillingCheckout(plan) {
    const res = await fetch('/api/console/billing/checkout', {
        method: 'POST',
        credentials: 'include',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ plan }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data?.error || data?.message || `Checkout failed (${res.status})`);
    }
    return data;
}

/**
 * Fetch billing details for the current user's organization.
 * Returns subscription access data combined with Stripe billing details
 * (payment method, invoices, payment history) when available.
 */
export async function getBillingDetails() {
    const res = await fetch('/api/console/billing/details', {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data?.error || data?.message || `Failed to fetch billing details (${res.status})`);
    }
    return data;
}

/**
 * Create a Stripe Customer Portal session.
 * Returns { portal_url } - redirect the user to manage their billing.
 */
export async function createBillingPortalSession() {
    const res = await fetch('/api/console/billing/portal', {
        method: 'POST',
        credentials: 'include',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data?.error || data?.message || `Portal session failed (${res.status})`);
    }
    return data;
}

/**
 * Start an in-app card add/replace. Returns { client_secret } for Stripe
 * Elements to confirm the SetupIntent in the browser.
 */
export async function createCardSetupIntent() {
    const res = await fetch('/api/console/billing/setup-intent', {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data?.error || data?.message || `Card setup failed (${res.status})`);
    }
    return data;
}

/**
 * Make a confirmed payment method the default on file (and the subscription's
 * default). Called after Stripe Elements confirms the SetupIntent.
 * Returns { payment_method } summarizing the saved card.
 */
export async function setDefaultCard(paymentMethodId) {
    const res = await fetch('/api/console/billing/set-default-card', {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_method_id: paymentMethodId }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data?.error || data?.message || `Saving card failed (${res.status})`);
    }
    return data;
}

/**
 * Remove the card on file. Passing a paymentMethodId targets a specific card;
 * omitting it removes the current default.
 */
export async function deleteCard(paymentMethodId) {
    const res = await fetch('/api/console/billing/delete-card', {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(paymentMethodId ? { payment_method_id: paymentMethodId } : {}),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data?.error || data?.message || `Removing card failed (${res.status})`);
    }
    return data;
}

// ─── System Service Defaults (superuser) ───────────────────────────

export async function listSystemDefaults() {
    const resp = await request('/system/services/');
    return resp?.data || resp || [];
}

export async function updateSystemDefault(serviceType, inheritable) {
    return request(`/system/services/${encodeURIComponent(serviceType)}`, {
        method: 'PATCH',
        body: { inheritable },
    });
}

// ─── Invite Token ──────────────────────────────────────────────────

/** Validate an invite token. Returns { valid, email, name, org_id, org_name, reason } */
export async function validateInviteToken(token) {
    return request(`/invite/${encodeURIComponent(token)}`);
}

/** Complete an invite: link auth_provider_id and finalize. */
export async function completeInvite(token, authProviderID) {
    return request(`/invite/${encodeURIComponent(token)}/complete`, {
        method: 'POST',
        body: { auth_provider_id: authProviderID || '' },
    });
}
